import { useAppDispatch, useAppSelector } from 'app/store/store';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { getI18n, useTranslation } from 'react-i18next';
import styles from './RoutePlanner.module.scss';
import { Icon } from '../Icon/Icon';
import { useGetSimpleRouteMutation } from 'app/api/facade';
import { SimpleRouteRequest } from 'ais-facade-api/api';
import { InfoWrapper } from '../AISCard';

import { setRouteBuilded, setShowRouteBuilded } from 'app/utils/facadeReducer';
import moment from 'moment';
import 'moment/locale/ru';
import humanizeDuration from 'humanize-duration';
import { DistanceComponent } from '../';
import { Input } from '../Input';
import { useAlertHook } from '../../hooks/useAlertHook';

export const RoutePlanner = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const language = getI18n().resolvedLanguage;
  const { addAlert } = useAlertHook();
  const storeDistanceData = useAppSelector((state) => state.searchSlice.distanceBetween);
  const routeBuilded = useAppSelector((state) => state.facadeSlice.routeBuilded.route);

  const [speed, setSpeed] = useState<number>(10);

  const [getRoute, { data: routeData, ...routeRequest }] = useGetSimpleRouteMutation({
    fixedCacheKey: 'get-route-planner',
  });

  useEffect(() => {
    const warningMessage = routeData?.messages?.find((message) => message.type === 'warning');
    if (warningMessage) {
      addAlert({
        data: {
          messages: [{ description: warningMessage.description, type: warningMessage.type, code: warningMessage.code }],
        },
      });
    } else if (routeData?.route && routeData?.route.geometry !== 'MULTILINESTRING EMPTY') {
      dispatch(setRouteBuilded(routeData?.route));
    } else {
      dispatch(setRouteBuilded(null));
    }
  }, [routeData]);

  useEffect(() => {
    routeRequest.reset();
  }, [storeDistanceData]);

  useEffect(() => {
    if (typeof storeDistanceData.from?.lat !== 'number' || typeof storeDistanceData.from?.lon !== 'number') return;
    if (typeof storeDistanceData.to?.lat !== 'number' || typeof storeDistanceData.to?.lon !== 'number') return;
    const body: SimpleRouteRequest = {
      from: {
        latitude: storeDistanceData.from.lat,
        longitude: storeDistanceData.from.lon,
      },
      to: {
        latitude: storeDistanceData.to.lat,
        longitude: storeDistanceData.to.lon,
      },
    };
    getRoute(body);
  }, [storeDistanceData.to, storeDistanceData.from]);

  const travelTime = useMemo(() => {
    if (speed && typeof routeBuilded?.distanceNmi === 'number') {
      let res = '';
      const hours = routeBuilded.distanceNmi / Number(speed);
      const duration = moment.duration({ hours });

      if (!isFinite(hours) || hours <= 0 || duration.asMilliseconds() < 60000) return;

      if (duration.asHours() > 0 && duration.asHours() < 1) res += `${t('LessThenHour')}`;
      if (duration.asHours() > 1 && duration.asHours() < 25)
        res += humanizeDuration(duration.asMilliseconds(), { language: language, units: ['h'], round: true });
      if (duration.asHours() > 25 && duration.asHours() < 120)
        res += humanizeDuration(duration.asMilliseconds(), { language: language, units: ['d'], round: true });
      if (duration.asHours() > 120)
        res += humanizeDuration(duration.asMilliseconds(), { language: language, units: ['d', 'h'], round: true });

      return res;
    }
  }, [speed, routeBuilded, language]);

  return (
    <div className={styles.container}>
      <div className={styles.heading}>
        <Icon name="routes" className={styles.headingIcon} />
        <span>{t('Distances')}</span>
      </div>
      <InfoWrapper
        strong
        label={`${t('AverageSpeed')} (${t('VesselSpeedUnit')})`}
        value={
          <Input
            className={styles.value}
            type="number"
            name="speed"
            value={speed.toString()}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setSpeed(Number(e.target.value));
            }}
          />
        }
      />

      <DistanceComponent />
      <div className={styles.divider} />
      <div className={styles.route_info_container}>
        {routeBuilded ? (
          <div className={styles.route_info}>
            <InfoWrapper
              label={t('Distance')}
              strong
              value={`${routeBuilded.distanceNmi?.toFixed(0)} ${t('NM')} (${routeBuilded.distanceKm?.toFixed(1)} ${t('Km')})`}
            />
            <InfoWrapper strong label={t('TimeOnRoute')} value={travelTime} />
          </div>
        ) : routeRequest.isLoading ? (
          <span className={styles.loader}></span>
        ) : (
          <span className={styles.placeholder}>
            {t('From')} — {t('To')}
          </span>
        )}
      </div>

      <div className={styles.divider} />
      <div
        className={styles.aim_icon}
        aria-disabled={routeRequest.isLoading || routeRequest.isError || !routeBuilded?.geometry}
        onClick={() => {
          dispatch(setShowRouteBuilded(true));
        }}
      >
        <Icon name="aim" size="20px" color={routeBuilded ? 'var(--default-primary)' : 'var(--main-menu-disabled)'} />
      </div>
    </div>
  );
};
